import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

/**
 * Fil d'ariane accessible.
 * Usage :
 *   <Breadcrumb items={[
 *     { name: 'Accueil', to: '/' },
 *     { name: 'Chroniques', to: '/category/chroniques' },
 *     { name: 'Mon titre d’article' },  // pas de `to` = page courante
 *   ]} />
 */
const Breadcrumb = ({ items = [], className = '' }) => {
  if (!items.length) return null;

  // Balisage JSON-LD pour Google (Rich Result Breadcrumb)
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const ldItems = items.map((it, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: it.name,
    ...(it.to ? { item: `${origin}${it.to}` } : {}),
  }));
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: ldItems,
  };

  return (
    <>
      <nav
        aria-label="Fil d'ariane"
        className={`flex items-center flex-wrap gap-1 text-[11px] uppercase tracking-[0.18em] text-[#7a716c] ${className}`}
      >
        {items.map((it, idx) => {
          const isLast = idx === items.length - 1;
          return (
            <React.Fragment key={idx}>
              {it.to && !isLast ? (
                <Link
                  to={it.to}
                  className="hover:text-[#8b2c3f] transition-colors inline-flex items-center gap-1.5"
                >
                  {idx === 0 && <Home size={11} />}
                  {it.name}
                </Link>
              ) : (
                <span
                  aria-current={isLast ? 'page' : undefined}
                  className={`inline-flex items-center gap-1.5 ${isLast ? 'text-[#1a1414] font-semibold normal-case tracking-normal' : ''}`}
                >
                  {idx === 0 && <Home size={11} />}
                  {isLast ? truncate(it.name, 60) : it.name}
                </span>
              )}
              {!isLast && <ChevronRight size={12} className="text-[#c9a961]" />}
            </React.Fragment>
          );
        })}
      </nav>
      {/* SEO: BreadcrumbList schema.org */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </>
  );
};

function truncate(s, n) {
  if (!s) return s;
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

export default Breadcrumb;
